/**
 * Frontend mirror of the backend pricing helper
 * (audio-sanctum-backend-main/src/common/pricing.ts). Every on-screen price —
 * the plan configurator, the cart review, the License modal — derives from
 * this, so what the customer sees matches what the backend charges.
 *
 * KEY RULE — claims are PER CHANNEL: extra claim packs are billed on every
 * channel (× channels). Keep this 1:1 with the backend file.
 */

export interface SubscriptionPricingConfig {
  basePriceMonthly: number;
  includedChannels: number;
  includedClaims: number;
  additionalChannelPrice: number;
  additionalClaimsPrice: number;
  claimIncrement: number;
  annualDiscountPercent: number;
}

export interface LicensePricingConfig {
  singleLicensePrice: number; // Standard tier
  plusLicensePrice: number; // Plus tier
  wavLicensePrice: number;
}

export type LicenseTier = "standard" | "plus";

export const round2 = (n: number): number => Math.round(n * 100) / 100;

export function effectiveBaseMonthly(config: SubscriptionPricingConfig, isAnnual: boolean): number {
  return isAnnual
    ? round2(config.basePriceMonthly * (1 - config.annualDiscountPercent / 100))
    : config.basePriceMonthly;
}

export interface SubscriptionBreakdown {
  isAnnual: boolean;
  intervalMultiplier: number;
  channels: number;
  claims: number;
  extraChannels: number;
  extraClaimPacks: number;
  extraClaimsPerChannel: number;
  baseMonthly: number;
  channelAddonMonthly: number;
  claimsAddonMonthly: number;
  /** base + channel addon + claims addon, all monthly. */
  addonsMonthly: number;
  totalMonthly: number;
}

export function computeSubscriptionPricing(
  config: SubscriptionPricingConfig,
  params: { channels: number; claims: number; isAnnual: boolean },
): SubscriptionBreakdown {
  const channels = Math.max(config.includedChannels, Math.floor(params.channels || config.includedChannels));
  const claims = Math.max(config.includedClaims, Math.floor(params.claims || config.includedClaims));
  const isAnnual = !!params.isAnnual;

  const extraChannels = Math.max(0, channels - config.includedChannels);
  const extraClaimPacks = Math.max(
    0,
    Math.floor((claims - config.includedClaims) / config.claimIncrement),
  );
  const extraClaimsPerChannel = extraClaimPacks * config.claimIncrement;

  const baseMonthly = effectiveBaseMonthly(config, isAnnual);
  const channelAddonMonthly = extraChannels * config.additionalChannelPrice;
  // Per-channel: extra claim packs are billed on every channel.
  const claimsAddonMonthly = extraClaimPacks * config.additionalClaimsPrice * channels;
  const addonsMonthly = round2(channelAddonMonthly + claimsAddonMonthly);
  const totalMonthly = round2(baseMonthly + addonsMonthly);

  return {
    isAnnual,
    intervalMultiplier: isAnnual ? 12 : 1,
    channels,
    claims,
    extraChannels,
    extraClaimPacks,
    extraClaimsPerChannel,
    baseMonthly,
    channelAddonMonthly,
    claimsAddonMonthly,
    addonsMonthly,
    totalMonthly,
  };
}

/** Single YouTube video license price for a premium track, by its tier. */
export function singleLicensePriceForTier(config: LicensePricingConfig, tier: string | null | undefined): number {
  return tier === "plus" ? config.plusLicensePrice : config.singleLicensePrice;
}

/** Lowest single-license price across tiers — drives the "from $X" copy. */
export function fromSingleLicensePrice(config: LicensePricingConfig): number {
  return Math.min(config.singleLicensePrice, config.plusLicensePrice);
}
